import React from 'react';

import { format } from 'date-fns';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';

import { timeStringToDate } from 'Helpers/dateUtils';

import DatePickerField from 'CommonComponents/DatePickerField';
import SelectField from 'CommonComponents/SelectField';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import XMarkIcon from 'Assets/icons/circle-xmark.svg';

export const validate = (draft) => {
    const errors = {};
    if (!draft.venue_id) errors.venue_id = $t('Venue is required');
    if (!draft.date) errors.date = $t('Date is required');
    if (!draft.start_time) errors.start_time = $t('Start time is required');
    if (!draft.end_time) errors.end_time = $t('End time is required');
    if (!draft.schedule_type_id) errors.schedule_type_id = $t('Session type is required');
    if (draft.start_time && draft.end_time && draft.end_time <= draft.start_time) {
        errors.end_time = $t('End time must be after start time');
    }
    return errors;
};

const SessionsTableEditRow = ({
    session,
    draft,
    venues,
    scheduleTypes,
    isDirty,
    isSaving,
    onConfirm,
    onCancel,
    onChange,
    errors,
    onErrorsChange,
    bulkDeleteMode,
    showActionsColumn,
    eventStartDate,
    eventEndDate,
}) => {
    const venueOptions = venues.map((v) => ({ value: v.id, label: v.name }));
    const scheduleTypeOptions = scheduleTypes.map((t) => ({ value: t.id, label: t.name }));

    const parseDraftDate = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('-').map(Number);
        if (!day || !month || !year) return null;
        return new Date(year, month - 1, day);
    };

    const clearError = (field) => {
        if (errors[field]) onErrorsChange({ ...errors, [field]: null });
    };

    const handleDateChange = (_name, formattedValue) => {
        onChange('date', formattedValue);
        clearError('date');
    };

    const handleStartTimeChange = (date) => {
        if (!date) return;
        onChange('start_time', format(date, 'HH:mm'));
        clearError('start_time');
    };

    const handleEndTimeChange = (date) => {
        if (!date) return;
        onChange('end_time', format(date, 'HH:mm'));
        clearError('end_time');
    };

    const handleVenueChange = (selected) => {
        onChange('venue_id', selected ? selected.value : null);
        clearError('venue_id');
    };

    const handleScheduleTypeChange = (selected) => {
        onChange('schedule_type_id', selected ? selected.value : null);
        clearError('schedule_type_id');
    };

    const handleConfirmClick = () => {
        const validationErrors = validate(draft);
        if (Object.keys(validationErrors).length > 0) {
            onErrorsChange(validationErrors);
            return;
        }
        onErrorsChange({});
        onConfirm();
    };

    const timeInputClass = (hasError) =>
        `tw-block tw-w-full tw-min-w-20 tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-px-2 tw-py-1 tw-text-charcoal focus:tw-outline-0 ${hasError ? 'is-invalid border border-danger' : ''}`;

    return (
        <tr>
            {/* Checkbox placeholder */}
            <td className={bulkDeleteMode ? 'tw-hidden' : ''} />

            {/* Event/Sport — always read-only */}
            <td className="tw-text-sm tw-font-medium tw-text-charcoal">{session.sport?.name}</td>

            {/* Venue */}
            <td>
                <SelectField options={venueOptions} value={draft.venue_id} onChange={handleVenueChange} placeholder={$t('Venue')} height={32} />
                {errors.venue_id && <p className="tw-mt-0.5 tw-text-xs tw-text-red-500">{errors.venue_id}</p>}
            </td>

            {/* Date */}
            <td>
                <DatePickerField
                    name="date"
                    value={parseDraftDate(draft.date)}
                    onChange={handleDateChange}
                    valueFormat="dd-MM-yyyy"
                    dateFormat="dd/MM/yyyy"
                    min={eventStartDate}
                    max={eventEndDate}
                    className={`tw-block tw-w-full tw-min-w-28 tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-px-2 tw-py-1 tw-text-charcoal focus:tw-outline-0 ${errors.date ? 'is-invalid border border-danger' : ''}`}
                />
                {errors.date && <p className="tw-mt-0.5 tw-text-xs tw-text-red-500">{errors.date}</p>}
            </td>

            {/* Start time */}
            <td>
                <DatePicker
                    selected={timeStringToDate(draft.start_time)}
                    onChange={handleStartTimeChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    timeCaption={$t('Start Time')}
                    dateFormat="HH:mm"
                    className={timeInputClass(!!errors.start_time)}
                    autoComplete="off"
                />
                {errors.start_time && <p className="tw-mt-0.5 tw-text-xs tw-text-red-500">{errors.start_time}</p>}
            </td>

            {/* End time */}
            <td>
                <DatePicker
                    selected={timeStringToDate(draft.end_time)}
                    onChange={handleEndTimeChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={5}
                    timeCaption={$t('End Time')}
                    dateFormat="HH:mm"
                    className={timeInputClass(!!errors.end_time)}
                    autoComplete="off"
                />
                {errors.end_time && <p className="tw-mt-0.5 tw-text-xs tw-text-red-500">{errors.end_time}</p>}
            </td>

            {/* Session Type */}
            <td>
                <SelectField
                    options={scheduleTypeOptions}
                    value={draft.schedule_type_id}
                    onChange={handleScheduleTypeChange}
                    placeholder={$t('Session Type')}
                    height={32}
                />
                {errors.schedule_type_id && <p className="tw-mt-0.5 tw-text-xs tw-text-red-500">{errors.schedule_type_id}</p>}
            </td>

            {/* Actions */}
            {showActionsColumn && (
                <td>
                    <div className="tw-flex tw-items-center tw-gap-x-3.5">
                        <button type="button" className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-sm tw-font-semibold tw-text-red" onClick={onCancel}>
                            <XMarkIcon className="tw-h-3" />
                            <div>{$t('Cancel')}</div>
                        </button>
                        <button
                            type="button"
                            className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-sm tw-font-semibold tw-text-blue"
                            disabled={!isDirty || isSaving}
                            onClick={handleConfirmClick}
                        >
                            <CircleCheckIcon className="tw-h-3" />
                            <div>{$t('Save')}</div>
                        </button>
                    </div>
                </td>
            )}
        </tr>
    );
};

export default SessionsTableEditRow;
